import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ColumnMenuOption, LabraToolTipV2 } from '@labrav/react-components'
import { isEmpty } from 'lodash/fp'
import type { ChannelPartnerPrivateOffer } from '../../modules/channelPartnerPrivateOffer/reducer'
import { OfferStatus, OfferTypeRenew } from '../../modules/privateOffer/reducer'
import { isOfferExpired } from '../../common/utils/isOfferExpired'
import { getDeleteOfferDisabledTooltip } from './ViewChannelOffer/ChannelOfferHeader/ChannelOfferHeaderActions/helpers'
import { CHANNEL_OFFER_READ_ONLY_PERMISSION_MESSAGE } from './constants'

export const getLabraResaleAuthDetailPageUrl = (
  labraResellerAgreementId?: string
) => {
  if (labraResellerAgreementId) {
    return `${window.location.origin}/flyout/resale-authorizations/received/${labraResellerAgreementId}/details`
  }
  return ''
}

export const getStatusOffer = ({
  offerStatus: status,
  offerExpirationDate,
  draft: isDraft,
}: ChannelPartnerPrivateOffer) => {
  const offerExpiration = offerExpirationDate
    ? new Date(offerExpirationDate)
    : null

  if (
    status === OfferStatus.PRIVATE_OFFER_FAILED ||
    status === OfferStatus.PRIVATE_OFFER_RESTRCITED ||
    status === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS
  ) {
    return status
  }
  if (status === OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS && isDraft) {
    return OfferStatus.PRIVATE_OFFER_IN_DRAFT
  }
  if (offerExpiration && isOfferExpired(offerExpiration, status)) {
    return OfferStatus.PRIVATE_OFFER_EXPIRED
  }
  return status
}

export const getOfferTypeWithToolTip = (offerType: OfferTypeRenew | string) => {
  switch (offerType) {
    case OfferTypeRenew.NEW: {
      return (
        <LabraToolTipV2
          tooltipPlacement="bottom"
          description={'This is a channel offer for a new buyer.'}
        >
          <div>New</div>
        </LabraToolTipV2>
      )
    }
    case OfferTypeRenew.RENEWAL: {
      return (
        <LabraToolTipV2
          tooltipPlacement="bottom"
          description={
            'This is an Agreement Based Offer sent \
            to a buyer based on their existing contract \
            for renewals or upgrades'
          }
        >
          <div>ABO</div>
        </LabraToolTipV2>
      )
    }
    default: {
      return offerType
    }
  }
}

export const getCppoMenuOptions = (
  cppo: ChannelPartnerPrivateOffer,
  cloudType?: string,
  baseUrl?: string,
  canWriteMarketplace?: boolean,
  copied?: boolean
) => {
  const isDraft = !!cppo?.draft
  const isAboDraftOffer =
    isDraft &&
    cppo?.type === OfferTypeRenew.RENEWAL &&
    !isEmpty(cppo?.labraSubscriptionId)
  const awsRenewalDraftRedirectURL = `${baseUrl || '/flyout'}/subscriptions/${cppo?.labraSubscriptionId}/authorization/${cppo?.resaleAuthorizationId}/edit/${cppo?.id}`

  const editUrl = `${baseUrl}/authorization/${cppo?.resaleAuthorizationId}/channel-offers/${cppo?.id}/edit`

  const viewUrlPathName = `${baseUrl}/authorization/${cppo?.resaleAuthorizationId}/channel-offers/${cppo?.id}/detail`

  const offerLink = cppo.cloudOfferUrl
  const options: ColumnMenuOption[] = [
    {
      key: 'view-cppo-details',
      label: 'View offer details',
      disable: false,
      disabledTooltip: '',
      route: isAboDraftOffer
        ? awsRenewalDraftRedirectURL
        : isDraft
          ? editUrl
          : viewUrlPathName,
    },
    {
      key: 'copy-channel-offer-url',
      label: (
        <LabraToolTipV2
          description={`${copied ? 'Copied' : ''}`}
          tooltipPlacement="top"
          icon={
            <FontAwesomeIcon
              icon={faCheck}
              style={{ width: '12px', height: '12px' }}
            />
          }
          tooltipPlacmentGap="0"
        >
          Copy channel offer URL
        </LabraToolTipV2>
      ),
      disable: !(
        [
          OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
          OfferStatus.PRIVATE_OFFER_VIEWED,
          OfferStatus.PRIVATE_OFFER_ACCEPTED,
        ].includes(cppo?.offerStatus as OfferStatus) && !isEmpty(offerLink)
      ),
      disabledTooltip:
        cppo?.offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
          ? 'This offer has expired. Extend the offer expiry date and then copy the offer link.'
          : `The Channel Offer URL is not yet ready. It is in progress at ${cloudType}.`,
    },
    {
      key: 'Delete-offer',
      label: 'Delete offer',
      disable:
        !canWriteMarketplace ||
        !(
          cppo?.offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED ||
          cppo?.draft === true ||
          cppo?.offerStatus === OfferStatus.PRIVATE_OFFER_FAILED ||
          cppo?.offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED
        ),
      disabledTooltip: !canWriteMarketplace
        ? CHANNEL_OFFER_READ_ONLY_PERMISSION_MESSAGE
        : getDeleteOfferDisabledTooltip(cppo as never, canWriteMarketplace),
    },
  ]
  if (
    cppo?.offerStatus === OfferStatus.PRIVATE_OFFER_EMAIL_SENT ||
    cppo?.offerStatus === OfferStatus.PRIVATE_OFFER_VIEWED ||
    cppo?.offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
  ) {
    options.push({
      key: 'extend-offer-expiration-date',
      label: 'Extend offer expiration date',
      disable: !canWriteMarketplace,
      disabledTooltip: !canWriteMarketplace
        ? CHANNEL_OFFER_READ_ONLY_PERMISSION_MESSAGE
        : '',
    })
  }
  return options
}
